import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { invalidateToolRegistryQueries } from "./cache-utils";
import type { ToolRegistryRecord, ToolVersionRecord } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface CreateToolRegistryParams {
  workspaceId: number;
  name?: string;
  folderId?: number | null;
  toolDefinition: Record<string, unknown>;
  commitMessage?: string;
}

interface CreateToolRegistryResponse {
  success: boolean;
  tool_registry: ToolRegistryRecord;
  version: ToolVersionRecord;
}

interface UseCreateToolRegistryOptions {
  onSuccess?: (data: CreateToolRegistryResponse, variables: CreateToolRegistryParams) => void;
}

export const useCreateToolRegistry = (options?: UseCreateToolRegistryOptions) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      name,
      folderId,
      toolDefinition,
      commitMessage,
    }: CreateToolRegistryParams): Promise<CreateToolRegistryResponse> => {
      const res = await fetch(ENDPOINTS.tool_registry.list(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
          folder_id: folderId ?? null,
          tool_definition: toolDefinition,
          commit_message: commitMessage,
        }),
      });
      return handleResponse(res);
    },
    onSuccess: (data, variables) => {
      invalidateToolRegistryQueries(queryClient, variables.workspaceId);
      if (options?.onSuccess) {
        options.onSuccess(data, variables);
      } else {
        displayToast("Tool created", ToastType.success);
        navigate(`/workspace/${variables.workspaceId}/tool/${data.tool_registry.id}`);
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      displayErrorToast(message || "Failed to create tool");
    },
  });
};
